/**
 * EmptyState Component - 데이터 없음 · 결과 없음 · 빈 화면 안내 (그림 + 제목 + 설명 + 버튼)
 * 스타일: index.css "EmptyState" - empty-view-* 클래스
 *
 *   <EmptyState
 *     illust="no-data"                                   // 선택: no-data | search | canvas (없으면 그림 없이)
 *     title="등록된 이메일 커넥터가 없습니다."
 *     description="커넥터를 추가하면 메일을 보내고 받을 수 있어요."   // 선택
 *     action={<Button color="primary" size="sm">추가</Button>}   // 선택
 *     size="md"                                          // sm: 표 · 카드 안 / md: 패널(기본) / lg: 페이지 전체
 *   />
 *
 * - 그림 중 역할별 버전이 있는 것(canvas)은 로그인 역할(관리자 / 사용자)에 맞춰 자동 선택
 * - 테두리 · 배경 없음 (놓이는 곳의 모양을 따름), 위치 · 여백은 className 으로
 */

import { ReactNode } from 'react';
import { isAdminRole } from '../../utils/role';
import { authApi } from '../../api';
import noDataIllust from '../../assets/illustrations/no-data.svg';
import emptySearchIllust from '../../assets/illustrations/empty-search.svg';
import adminEmptyCanvasIllust from '../../assets/illustrations/admin-empty-canvas.svg';
import userEmptyCanvasIllust from '../../assets/illustrations/user-empty-canvas.svg';

export type EmptyStateIllust = 'no-data' | 'search' | 'canvas';

// 그림 - 문자열 = 공용, { admin, user } = 역할별
const ILLUSTS: Record<EmptyStateIllust, string | { admin: string; user: string }> = {
  'no-data': noDataIllust,
  search: emptySearchIllust,
  canvas: { admin: adminEmptyCanvasIllust, user: userEmptyCanvasIllust },
};

interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  illust?: EmptyStateIllust;
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function EmptyState({
  title,
  description,
  illust,
  action,
  size = 'md',
  className = '',
}: EmptyStateProps) {
  const src = illust ? ILLUSTS[illust] : undefined;
  const illustSrc =
    typeof src === 'object'
      ? (isAdminRole(authApi.getUser()?.role) ? src.admin : src.user)
      : src;

  return (
    <div className={`empty-view empty-view--${size} ${className}`.trim()}>
      {illustSrc && <img className="empty-view-illust" src={illustSrc} alt="" />}
      <p className="empty-view-title">{title}</p>
      {description && <p className="empty-view-desc">{description}</p>}
      {action && <div className="empty-view-action">{action}</div>}
    </div>
  );
}

export default EmptyState;
